import './PartyPackageMenu.css';
import React, { useState, useMemo } from 'react';
import { RiCheckboxBlankCircleLine } from "react-icons/ri";
import { API_BASE_URL } from '../../utils';
import { fi } from 'date-fns/locale';

export default function ClubMenuCards({ menus = [] }) {
    const [selectedCategory, setSelectedCategory] = useState('All');

    const menuData = menus.length > 0 ? menus[0].category : {};

    const categories = useMemo(() => {
        const validCategories = Object.entries(menuData)
            .filter(([key, items]) => {
                const choice = items?.veg_choice || items?.nonveg_choice || items?.item_choice;
                return key !== 'clubLogo' && key !== 'category' && choice !== "0" && choice !== 0;
            })
            .map(([key]) => key);
        return ['All', ...validCategories];
    }, [menuData]);

    const filteredMenus = useMemo(() => {
        const entries = Object.entries(menuData).filter(([key, items]) => {
            const choice = items?.veg_choice || items?.nonveg_choice || items?.item_choice;
            return key !== 'clubLogo' && key !== 'category' && choice !== "0" && choice !== 0;
        });

        if (selectedCategory === 'All') return entries;
        return entries.filter(([key]) => key === selectedCategory);
    }, [selectedCategory, menuData]);

    return (
        <div className="cmc-gallery-empty-container">
            {!menus || menus.length === 0 || !menus[0]?.category || filteredMenus.length === 0 ? (
                <div className="cmc-gallery-empty-content">
                    <h1>No Menu Cards Available</h1>
                    <p>We're working on an exciting menu to give you the best value and experience.</p>
                    <img
                        src="https://illustrations.popsy.co/gray/web-design.svg"
                        alt="Coming Soon"
                        className="cmc-gallery-empty-image"
                    />
                </div>
            ) : (
                <div className="cmc-gallery-container">
                    <div className="cmc-gallery-filters">
                        {categories.map((cat, i) => (
                            <span
                                key={i}
                                className={`cmc-filter-button ${selectedCategory === cat ? 'cmc-active' : ''}`}
                                onClick={() => setSelectedCategory(cat)}
                            >
                                {cat.replace(/_/g, ' ').toUpperCase()}
                            </span>
                        ))}
                    </div>

                    <div className="cmc-gallery-content">
                        {filteredMenus.map(([category, items], index) => {
                            const formatCategory = category.replace(/_/g, ' ').toUpperCase();
                            const vegCount = parseInt(items?.veg_choice || items?.veg?.length || "0");
                            const nonVegCount = parseInt(items?.nonveg_choice || items?.nonveg?.length || "0");
                            const itemCount = parseInt(items?.item_choice || items?.item?.length || "0");

                            // Check if this category has any valid data to show
                            const hasVegData = items?.veg?.length > 0 && vegCount > 0;
                            const hasNonVegData = items?.nonveg?.length > 0 && nonVegCount > 0;
                            const hasItemData = !items?.veg && !items?.nonveg && items?.item?.length > 0 && itemCount > 0;
                            
                            // Only render category if it has at least one section with data
                            if (!hasVegData && !hasNonVegData && !hasItemData) {
                                return null;
                            }

                            return (
                                <div key={index} className="cmc-category-box">
                                    <div className="cmc-category-header">
                                        <h2>{formatCategory}</h2>
                                    </div>

                                    <div className="cmc-item-type-section">
                                        {/* VEG Section - Only show if vegCount > 0 and veg items exist */}
                                        {hasVegData && (
                                            <div className="cmc-type-box">
                                                <h3 className="cmc-type-title">Veg ({vegCount} Choices)</h3>
                                                <div className="cmc-item-grid">
                                                    {items.veg.map((item, i) => (
                                                        <div key={`veg-${i}`} className="cmc-item-card veg">
                                                            <div className='cmc-veg-img-container'>
                                                                <img src="https://images.unsplash.com/photo-1712757248842-04f6e3844374?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTExfHx2ZWclMjBmb29kJTIwaW5kaWFufGVufDB8fDB8fHww" alt="" />
                                                                <div className="cmc-tag">Veg</div>
                                                            </div>
                                                            <div className='cmc-veg-text-container'>
                                                                <div className="cmc-item-text">{item}</div>
                                                                <div className="cmc-item-text-dis">Description</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* NON-VEG Section - Only show if nonVegCount > 0 and nonveg items exist */}
                                        {hasNonVegData && (
                                            <div className="cmc-type-box">
                                                <h3 className="cmc-type-title">Non-Veg ({nonVegCount} Choices)</h3>
                                                <div className="cmc-item-grid">
                                                    {items.nonveg.map((item, i) => (
                                                        <div key={`nonveg-${i}`} className="cmc-item-card nonveg">
                                                            <div className='cmc-veg-img-container'>
                                                                <img src="https://images.unsplash.com/photo-1680529672551-16132239d69b?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfDN8fHxlbnwwfHx8fHw%3D" alt="" />
                                                                <div className="cmc-tag">Non-Veg</div>
                                                            </div>
                                                            <div className='cmc-veg-text-container'>
                                                                <div className="cmc-item-text">{item}</div>
                                                                <div className="cmc-item-text-dis">Description</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* ITEM Section - Only show if itemCount > 0 and item items exist */}
                                        {hasItemData && (
                                            <div className="cmc-type-box">
                                                <h3 className="cmc-type-title">Items ({itemCount} Choices)</h3>
                                                <div className="cmc-item-grid">
                                                    {items.item.map((item, i) => (
                                                        <div key={`item-${i}`} className="cmc-item-card other">
                                                            <div className="cmc-tag">Item</div>
                                                            <div className="cmc-item-text">{item}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}